import {
  IconName16x16,
  IconName20x20,
  iconRegistry16x16,
  iconRegistry20x20,
} from '@fullstory/icons';
import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef, HTMLAttributes, useEffect } from 'react';
import { identifyComponent } from '../../util';
import colors from './colors.module.scss';
import css from './index.module.scss';

export type IconSize = 16 | 20;

export type IconName = IconName16x16 | IconName20x20;

export interface IconProps
  extends Omit<HTMLAttributes<SVGSVGElement>, 'className' | 'css' | 'width' | 'height'> {
  /**
   * The name of the icon you want to render.
   */
  name: IconName;
  /**
   * The size of the icon. Supported sizes are "small" (16x16) and "large" (20x20).
   *
   * @default 'small'
   */
  size?: IconSize;
  /**
   * The color of the icon. Supported colors are all of the object colors in the theme.
   *
   * Defaults to `'inherit'` which hoists control of the icon color to the parent CSS;
   * this is useful if there are active/hover interactions and/or you need to color-match
   * the icon with supporting text.
   *
   * @default 'inherit'
   */
  color?: keyof typeof colors;
  /**
   * An optional test ID to identify your component in tests.
   *
   * @default 'icon'
   */
  testId?: string;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ name, size = 16, color = 'inherit', testId = 'icon', ...rest }, ref) => {
    const IconComponent = getIconComponent(size, name);

    useEffect(() => {
      if (!IconComponent)
        console.error(
          `Icon Component Error: The icon registry does not have an icon for name: ${name}.`,
        );
    }, [IconComponent, name]);

    return IconComponent ? (
      <IconComponent
        data-component="Icon"
        data-description={name}
        ref={ref}
        aria-hidden="true"
        role="img"
        data-testid={testId}
        className={classNames(css.icon, colors[color])}
        width={size}
        height={size}
        {...rest}
        {...identifyComponent('icon', rest)}
      />
    ) : null;
  },
);

Icon.displayName = 'Icon';

/**
 * Returns the correct icon from the icon library given a size and name.
 */
function getIconComponent(size: IconSize, name: IconName) {
  if (size === 16) return iconRegistry16x16[name];
  return has20x20Icon(name) ? iconRegistry20x20[name] : iconRegistry16x16[name];
}

/**
 * Type guard that determines if a 20x20 icon exists for the given name.
 */
const has20x20Icon = (name: IconName): name is IconName20x20 =>
  (name as IconName20x20) in iconRegistry20x20;
